import { TbHelpCircleFilled, TbLoader2 } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { usePromptBarContext } from "../PromptBarContext";

/**
 * Clean toggle for Improve Prompts functionality
 * Uses clickable status badges matching the visual style
 */
export const ImprovePromptsToggle: Component = () => {
  const ctx = usePromptBarContext();
  const { improveState, improveLoading } = ctx.autoImprove;

  return (
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-primary-text">Improve Prompts</span>
        <a
          class="inline-flex items-center text-secondary-text hover:text-brand transition-colors"
          href="https://go.storytell.ai/learn-improve-prompt"
          target="_blank"
          rel="noreferrer"
          title="Learn more about Improve Prompts"
        >
          <TbHelpCircleFilled class="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Status Badge - Shows current state */}
      <div class="flex items-center gap-1.5 px-2 py-1 rounded-full border border-muted-border">
        <span
          class={twMerge(
            "h-5 w-7 rounded-full grid place-content-center uppercase tracking-wide text-[0.45rem] font-bold transition-all",
            improveState() === "on" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100",
            improveState() === "off" && "bg-black/5 dark:bg-white/5 text-secondary-text",
            improveState() === "auto" && "bg-violet-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
          )}
        >
          <Show when={improveLoading()} fallback={improveState()}>
            <TbLoader2 class="animate-spin size-3" />
          </Show>
        </span>
      </div>
    </div>
  );
};

/**
 * Interactive mode selector with clickable badges
 */
export const ImprovePromptsInfo: Component = () => {
  const ctx = usePromptBarContext();
  const { improveState, improveLoading, setImproveState } = ctx.autoImprove;

  const modes = [
    {
      value: "off" as const,
      label: "OFF",
      description: "No prompt suggestions",
      baseClasses: "bg-black/5 dark:bg-white/5 text-secondary-text",
      activeClasses: "ring-1 bg-black/5 dark:bg-white/5 ring-black/10 dark:ring-white/10",
    },
    {
      value: "on" as const,
      label: "ON",
      description: "Suggests improved prompts for review",
      baseClasses: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400",
      activeClasses:
        "ring-1 bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 ring-emerald-200 dark:ring-emerald-800",
    },
    {
      value: "auto" as const,
      label: "AUTO",
      description: "Automatically applies improvements",
      baseClasses: "bg-violet-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400",
      activeClasses:
        "ring-1 bg-violet-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 ring-purple-200 dark:ring-purple-800",
    },
  ];

  return (
    <div class="space-y-2 p-2">
      <a
        class="flex items-center justify-end gap-1 underline text-brand text-xs"
        href="https://go.storytell.ai/learn-improve-prompt"
        target="_blank"
        rel="noreferrer"
      >
        <TbHelpCircleFilled />
        Learn more
      </a>

      <For each={modes}>
        {(mode) => {
          const isActive = () => improveState() === mode.value;
          const isLoading = () => improveLoading() && isActive();

          return (
            <button
              type="button"
              onClick={() => setImproveState(mode.value)}
              disabled={improveLoading()}
              class={twMerge(
                "flex items-start gap-2 w-full cursor-pointer p-1 rounded-lg transition-all duration-200 hover:bg-canvas dark:hover:bg-panel",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
              aria-pressed={isActive()}
              aria-label={`Select ${mode.label} mode`}
            >
              <div
                class={twMerge(
                  "w-7 h-5 rounded-full grid place-content-center uppercase tracking-wide text-[0.45rem] font-bold transition-all duration-200 cursor-pointer",
                  isActive() ? mode.activeClasses : mode.baseClasses,
                  !isActive() && !improveLoading() && "hover:opacity-80",
                  isActive() && "shadow-sm",
                )}
              >
                <Show when={isLoading()} fallback={mode.label}>
                  <TbLoader2 class="animate-spin size-3" />
                </Show>
              </div>
              <div class="text-left">
                <div
                  class={twMerge(
                    "text-xs font-medium transition-colors duration-200",
                    isActive() ? "text-primary-text" : "text-primary-text/70",
                  )}
                >
                  {mode.value === "off" ? "Disabled" : mode.value === "on" ? "Enabled" : "Automatically"}
                </div>
                <div
                  class={twMerge(
                    "text-xs transition-colors duration-200",
                    isActive() ? "text-secondary-text" : "text-secondary-text/60",
                  )}
                >
                  {mode.description}
                </div>
              </div>
            </button>
          );
        }}
      </For>
    </div>
  );
};
